import { memo, useMemo } from 'react';
import { Box, Flex, Image, IconButton } from '@chakra-ui/react';
import { MdVerified } from 'react-icons/md';
import PlaceHolder from 'assets/placeholder.png';

/* ─────────────────────────────────────────────────────────────
   BRAND TOKENS
   ───────────────────────────────────────────────────────────── */
const NAVY    = '#1B3B5F';
const INK     = '#1A2740';
const BORDER  = '#E8EDF5';
const DANGER  = '#E53E3E';
const INFO    = '#2563EB';

const FONT = "'Bricolage Grotesque', sans-serif";

/* ─────────────────────────────────────────────────────────────
   ICONS
   ───────────────────────────────────────────────────────────── */
const ChevronLeft = (props) => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" {...props}>
        <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const ChevronRight = (props) => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" {...props}>
        <path d="M9 6L15 12L9 18" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

/* ─────────────────────────────────────────────────────────────
   PRODUCT IMAGE GALLERY — ASOS-style, flat & clean
   - No outer card. No nested backgrounds. No multiple layers.
   - Thumbnails sit naked next to the image (no wrapping card)
   - Image fills its own region, no internal padding
   ───────────────────────────────────────────────────────────── */

const ProductImageGallery = memo(function ProductImageGallery({
    mainImage,
    images = [],
    discountPercentage,
    isVerified,
    moreImagesLink,
    onSelectImage,
}) {
    const galleryImages = useMemo(() => {
        if (images.length > 0) return images;
        return mainImage ? [mainImage] : [];
    }, [images, mainImage]);

    const hasMultiple = galleryImages.length > 1;

    const currentIdx = useMemo(() => {
        const idx = galleryImages.findIndex((img) => img === mainImage);
        return idx >= 0 ? idx : 0;
    }, [galleryImages, mainImage]);

    const goPrev = () => {
        if (!hasMultiple) return;
        const prev = currentIdx <= 0 ? galleryImages.length - 1 : currentIdx - 1;
        onSelectImage?.(galleryImages[prev]);
    };

    const goNext = () => {
        if (!hasMultiple) return;
        const next = currentIdx >= galleryImages.length - 1 ? 0 : currentIdx + 1;
        onSelectImage?.(galleryImages[next]);
    };

    return (
        <Box w="full" fontFamily={FONT}>
            <Flex
                direction={{ base: 'column', md: 'row' }}
                gap={{ base: '10px', md: '14px' }}
                align="stretch"
            >
                {/* ── VERTICAL THUMBNAIL STRIP (desktop) ──
                    Naked thumbnails — no wrapping card, just stacked images */}
                <Box
                    display={{ base: 'none', md: 'flex' }}
                    flexDirection="column"
                    gap="8px"
                    flexShrink={0}
                    maxH="520px"
                    overflowY="auto"
                    css={{
                        '&::-webkit-scrollbar': { width: '3px' },
                        '&::-webkit-scrollbar-thumb': {
                            background: BORDER,
                            borderRadius: '4px',
                        },
                    }}
                >
                    {galleryImages.map((image, i) => (
                        <Box
                            key={`${image}-${i}`}
                            as="button"
                            type="button"
                            w="68px"
                            h="68px"
                            flexShrink={0}
                            borderRadius="6px"
                            overflow="hidden"
                            cursor="pointer"
                            border="1.5px solid"
                            borderColor={mainImage === image ? INK : BORDER}
                            transition="border-color 0.15s ease"
                            bg="white"
                            _hover={{ borderColor: INK }}
                            _focusVisible={{
                                outline: `2px solid ${NAVY}`,
                                outlineOffset: '2px',
                            }}
                            onClick={() => onSelectImage?.(image)}
                            aria-label={`View image ${i + 1} of ${galleryImages.length}`}
                            aria-pressed={mainImage === image}
                        >
                            <Image
                                w="full"
                                h="full"
                                objectFit="cover"
                                src={image}
                                fallbackSrc={PlaceHolder}
                                alt=""
                            />
                        </Box>
                    ))}
                </Box>

                {/* ── MAIN IMAGE ──
                    Sits flat on the page. Bounded height, contain fit.
                    No card wrapper, no internal bg color tints. */}
                <Box flex="1" minW="0" position="relative">
                    <Box
                        // bg="white"
                        borderRadius="8px"
                        overflow="hidden"
                        position="relative"
                        minH={{ base: '320px', md: '440px' }}
                        maxH={{ base: '460px', md: '560px' }}
                        w="full"
                    >
                        <Image
                            key={mainImage}
                            src={mainImage}
                            objectFit="contain"
                            w="full"
                            h="full"
                            minH={{ base: '320px', md: '440px' }}
                            maxH={{ base: '460px', md: '560px' }}
                            fallbackSrc={PlaceHolder}
                            alt="Product image"
                        />

                        {/* Prev / Next arrows  */}
                        {hasMultiple && (
                            <>
                                <IconButton
                                    aria-label="Previous image"
                                    icon={<ChevronLeft />}
                                    onClick={goPrev}
                                    position="absolute"
                                    left="10px"
                                    top="50%"
                                    transform="translateY(-50%)"
                                    bg="rgba(255,255,255,0.9)"
                                    color={INK}
                                    borderRadius="full"
                                    w="36px"
                                    h="36px"
                                    minW="36px"
                                    boxShadow="0 1px 4px rgba(0,0,0,0.1)"
                                    _hover={{
                                        bg: 'white',
                                        color: NAVY,
                                    }}
                                    transition="all 0.15s ease"
                                    zIndex={2}
                                />
                                <IconButton
                                    aria-label="Next image"
                                    icon={<ChevronRight />}
                                    onClick={goNext}
                                    position="absolute"
                                    right="10px"
                                    top="50%"
                                    transform="translateY(-50%)"
                                    bg="rgba(255,255,255,0.9)"
                                    color={INK}
                                    borderRadius="full"
                                    w="36px"
                                    h="36px"
                                    minW="36px"
                                    boxShadow="0 1px 4px rgba(0,0,0,0.1)"
                                    _hover={{
                                        bg: 'white',
                                        color: NAVY,
                                    }}
                                    transition="all 0.15s ease"
                                    zIndex={2}
                                />
                            </>
                        )}

                        {/* Discount badge */}
                        {discountPercentage > 0 && (
                            <Box
                                position="absolute"
                                top="12px"
                                left="12px"
                                bg={DANGER}
                                color="white"
                                borderRadius="4px"
                                px="9px"
                                py="3px"
                                fontSize="11px"
                                fontWeight="800"
                                letterSpacing="0.04em"
                                zIndex={2}
                            >
                                -{discountPercentage}% OFF
                            </Box>
                        )}

                        {/* Verified badge */}
                        {isVerified && (
                            <Box
                                position="absolute"
                                top="12px"
                                right="12px"
                                bg="white"
                                borderRadius="full"
                                p="3px"
                                boxShadow="0 1px 4px rgba(0,0,0,0.1)"
                                zIndex={2}
                            >
                                <MdVerified size={20} color={INFO} title="Verified vendor" />
                            </Box>
                        )}

                        {/* Image counter */}
                        {hasMultiple && (
                            <Box
                                position="absolute"
                                bottom="12px"
                                right="12px"
                                bg="rgba(26, 39, 64, 0.8)"
                                color="white"
                                borderRadius="full"
                                px="10px"
                                py="3px"
                                fontSize="11px"
                                fontWeight="700"
                                zIndex={2}
                            >
                                {currentIdx + 1} / {galleryImages.length}
                            </Box>
                        )}
                    </Box>
                </Box>
            </Flex>

            {/* ── MOBILE THUMBNAIL STRIP ──
                Sits below image, no card, just naked thumbnails */}
            {galleryImages.length > 0 && (
                <Box
                    mt="10px"
                    display={{ base: 'block', md: 'none' }}
                    overflowX="auto"
                    css={{
                        '&::-webkit-scrollbar': { display: 'none' },
                        scrollbarWidth: 'none',
                    }}
                >
                    <Flex gap="8px" flexWrap="nowrap" width="max-content">
                        {galleryImages.map((image, i) => (
                            <Box
                                key={`${image}-m-${i}`}
                                as="button"
                                type="button"
                                w="60px"
                                h="60px"
                                flexShrink={0}
                                borderRadius="6px"
                                overflow="hidden"
                                cursor="pointer"
                                border="1.5px solid"
                                borderColor={mainImage === image ? INK : BORDER}
                                transition="border-color 0.15s ease"
                                onClick={() => onSelectImage?.(image)}
                                aria-label={`View image ${i + 1} of ${galleryImages.length}`}
                                aria-pressed={mainImage === image}
                            >
                                <Image
                                    w="full"
                                    h="full"
                                    objectFit="cover"
                                    src={image}
                                    fallbackSrc={PlaceHolder}
                                    alt=""
                                />
                            </Box>
                        ))}
                    </Flex>
                </Box>
            )}

            {moreImagesLink && (
                <Box mt="10px">
                    <Box
                        as="button"
                        type="button"
                        onClick={() => window.open(moreImagesLink, '_blank', 'noopener,noreferrer')}
                        fontSize="12px"
                        fontWeight="600"
                        color={NAVY}
                        textDecoration="underline"
                        cursor="pointer"
                        _hover={{ color: INK }}
                    >
                        View more images
                    </Box>
                </Box>
            )}
        </Box>
    );
});

export default ProductImageGallery;